import { z } from 'zod';
import { IsoDateTimeSchema, LanguageCodeSchema, TenantIdSchema, UuidSchema } from './common';

export const PublicationStatusSchema = z.enum(['draft', 'review', 'published', 'retired']);
export type PublicationStatus = z.infer<typeof PublicationStatusSchema>;

export const SourceFormatSchema = z.enum(['epub2', 'epub3', 'mobi', 'azw3', 'kf8', 'pdf', 'fb2', 'cbz']);
export type SourceFormat = z.infer<typeof SourceFormatSchema>;

export const ContributorRoleSchema = z.enum([
  'author',
  'editor',
  'translator',
  'illustrator',
  'narrator',
  'foreword',
  'cover',
]);
export type ContributorRole = z.infer<typeof ContributorRoleSchema>;

export const ContributorSchema = z.object({
  name: z.string().min(1).max(200),
  role: ContributorRoleSchema.default('author'),
  // A backend explicit `null`-t küld a sort_name-re ha nincs — `nullish()`
  // engedi mind a `null`-t, mind a hiányzó mezőt.
  sortName: z.string().max(200).nullish(),
  // A SQLAlchemy ORM-en keresztül néha jön egy `id` mező is — az engedélyezzük.
  id: UuidSchema.optional(),
});
export type Contributor = z.infer<typeof ContributorSchema>;

export const PublicationSchema = z.object({
  id: UuidSchema,
  tenantId: TenantIdSchema,
  slug: z.string().min(1).max(200),
  title: z.string().min(1).max(500),
  subtitle: z.string().max(500).nullable().optional(),
  description: z.string().max(10_000).nullable().optional(),
  language: LanguageCodeSchema,
  contributors: z.array(ContributorSchema).default([]),
  isbn13: z.string().regex(/^\d{13}$/).nullable().optional(),
  publisher: z.string().max(200).nullable().optional(),
  publishedAt: IsoDateTimeSchema.nullable().optional(),
  tags: z.array(z.string().max(64)).default([]),
  collections: z.array(UuidSchema).default([]),
  status: PublicationStatusSchema,
  coverUrl: z.string().url().nullable().optional(),
  // A létrehozó/feltöltő user UUID-ja (== JWT sub). Régi soroknál null.
  // A frontend ebből dönti el, mutassa-e a törlés gombot a könyvtárosnak.
  createdBy: UuidSchema.nullable().optional(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Publication = z.infer<typeof PublicationSchema>;

export const CreatePublicationSchema = PublicationSchema.pick({
  title: true,
  subtitle: true,
  description: true,
  language: true,
  contributors: true,
  isbn13: true,
  publisher: true,
  tags: true,
}).extend({
  slug: z.string().min(1).max(200).optional(),
});
export type CreatePublicationInput = z.infer<typeof CreatePublicationSchema>;

export const UpdatePublicationSchema = CreatePublicationSchema.partial();
export type UpdatePublicationInput = z.infer<typeof UpdatePublicationSchema>;

export const PublicationListQuerySchema = z.object({
  cursor: z.string().nullable().optional(),
  limit: z.number().int().min(1).max(100).default(24),
  status: PublicationStatusSchema.optional(),
  search: z.string().max(200).optional(),
  tag: z.string().max(64).optional(),
  language: LanguageCodeSchema.optional(),
  sort: z.enum(['createdAt', 'updatedAt', 'title', 'publishedAt']).default('updatedAt'),
  dir: z.enum(['asc', 'desc']).default('desc'),
});
export type PublicationListQuery = z.infer<typeof PublicationListQuerySchema>;

/**
 * Egy konkrét manifesztáció (pl. konvertált EPUB 3 verzió).
 * Egy publikációhoz több is tartozhat: az eredeti + a normalizált EPUB 3.
 */
export const ManifestationSchema = z.object({
  id: UuidSchema,
  publicationId: UuidSchema,
  format: z.literal('epub3'),
  sourceFormat: SourceFormatSchema,
  hash: z.string().regex(/^[a-f0-9]{64}$/, 'sha256 kell'),
  bytes: z.number().int().nonnegative(),
  explodedUri: z.string().url(),
  manifestUri: z.string().url(),
  /** A többi manifesztáció közül a frissítendő/elsődleges (csak egy igaz) */
  isPrimary: z.boolean(),
  validation: z
    .object({
      valid: z.boolean(),
      epubcheckVersion: z.string(),
      errors: z.number().int().nonnegative(),
      warnings: z.number().int().nonnegative(),
    })
    .nullable(),
  createdAt: IsoDateTimeSchema,
});
export type Manifestation = z.infer<typeof ManifestationSchema>;
